import createClient, { type Client, type ClientOptions } from 'openapi-fetch';
import { networkError, toApiError } from './errors';
import type { paths } from './schema';

export type MasarClient = Client<paths>;

/** Sent on every request; the API rejects mutations without it (CSRF). */
export const REQUESTED_WITH = { name: 'X-Requested-With', value: 'masar' } as const;

export interface MasarClientOptions {
  /**
   * Browser: "/api" (same origin; Caddy strips the prefix). Server side:
   * the internal API URL without the prefix, e.g. "http://api:8080".
   */
  baseUrl?: string;
  /** The UI language, sent as Accept-Language so problem titles match. */
  locale?: () => string;
  /** Called on any 401, e.g. to send staff to the sign-in page. */
  onUnauthorized?: (response: Response) => void;
  fetch?: ClientOptions['fetch'];
  /** Second argument to fetch, e.g. Next.js { next: { revalidate } }. */
  requestInitExt?: Record<string, unknown>;
  headers?: Record<string, string>;
}

export function createMasarClient(opts: MasarClientOptions = {}): MasarClient {
  const client = createClient<paths>({
    baseUrl: opts.baseUrl ?? '/api',
    credentials: 'include',
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
    ...(opts.requestInitExt ? { requestInitExt: opts.requestInitExt } : {}),
    ...(opts.headers ? { headers: opts.headers } : {}),
  });
  client.use({
    onRequest({ request }) {
      request.headers.set(REQUESTED_WITH.name, REQUESTED_WITH.value);
      if (!request.headers.has('Accept-Language')) {
        request.headers.set('Accept-Language', opts.locale?.() ?? 'ar');
      }
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401) opts.onUnauthorized?.(response);
      return undefined;
    },
    onError({ error }) {
      return networkError(error);
    },
  });
  return client;
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Awaits an openapi-fetch call and returns its data, or throws an ApiError. */
export async function call<T>(p: Promise<FetchResult<T>>): Promise<T> {
  const r = await p;
  if (!r.response.ok) throw toApiError(r.response, r.error);
  return r.data as T;
}

export interface WithEtag<T> {
  data: T;
  /** Exactly what the API sent in ETag; send it back as If-Match. */
  etag: string | undefined;
}

/** Like call(), and keeps the ETag for a later If-Match. */
export async function callWithEtag<T>(p: Promise<FetchResult<T>>): Promise<WithEtag<T>> {
  const r = await p;
  if (!r.response.ok) throw toApiError(r.response, r.error);
  return { data: r.data as T, etag: r.response.headers.get('ETag') ?? undefined };
}

/**
 * The If-Match value for a record read from a list (records carry `version`).
 * Prefer the ETag a GET returned; this builds the same "v<version>" tag.
 */
export function etagOf(version: number): string {
  return `"v${version}"`;
}
